import * as React from 'react';
import { ButtonToolbar, ButtonGroup, Button } from 'reactstrap';
import classNames from 'classnames'
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"
import { debounce } from 'perfect-debounce';
import AccentIcons from '../../icons/Icons';
import { accentUtils, newGuid, from, t, compareObjects, showDialog } from '../../services/HelperService';
import { configFrom } from '../../services/ProductService';
import { ConfiguratorContext } from './ConfigContextProvider';
import { PanelPatternActionBtn } from './ConfigPanelPattern';
import { ValueActionBtn } from './SelectValueDlg';
import { FabricDetailsActionBtn } from './SelectFabricDlg';
import { AccentButton } from '../AccentButton';
import { ApplyChangesErrorDlg } from './ApplyChangesErrorDlg';
import userService from '../../services/UserService';
import { AccentPopup } from '../../dialogs/AccentPopup';
import { useAccentDisplay } from '../AccentDisplay';




export const fabricSingleLineDisplayType = "SoftFurnishings.Controls.FabricOptionCtrl, SoftFurnishings";
export const fabricTwoLineDisplayType = "SoftFurnishings.Controls.FabricOption2LineCtrl, SoftFurnishings";
export const panelPatternDisplayType = "InsyteProductModule.Controls.PanelPatternOptionCtrl, InsyteProductModule";
// Checkbox options the SWF import marks as one-shot commands (CustomDisplayType); rendered
// as a button that pulses the value instead of a checkbox. Never user-selectable in the
// designer - only the SWF import writes this marker.
export const momentaryButtonDisplayType = "SWF.MomentaryButton";


export const configEvents = {

    controlGotFocus: "config_control_got_focus",
    controlLostFocus: "config_control_lost_focus",

};

export const configEventControlType = {
    singleSize: "single_size",
    decimal: "decimal",
    numeric: "numeric",
    text: "text",
    picklist: "picklist",
};





function setOptionValueChangedForConfig(config, optionModel, tag, userChanged) {

    if (userChanged === undefined || userChanged === true) {
        optionModel.UserChanged = true;
    } 

    config.DoValueChangedWithValidation(optionModel, tag);
}

export function setOptionValueChanged(ctx, optionModel, tag, userChanged) {
    setOptionValueChangedForConfig(ctx.config, optionModel, tag, userChanged);
}

// Runs the option-change cascade after the browser has painted the control's new
// state. The rule cascade on rule-heavy products blocks the thread for hundreds of
// milliseconds; a click that runs it synchronously cannot show its selection until the
// cascade ends. One rAF + task yields exactly one frame; it is not a delay for its own
// sake. While the run is pending the config is marked in progress (the same counter
// saves wait on), so a save cannot serialise the value without its cascade. Repeated
// calls before the run coalesce into a single cascade for the final value.
export function createDeferredOptionValueChange() {
    let pending = null;
    const run = () => {
        const job = pending;
        pending = null;
        if (!job) return;
        try {
            setOptionValueChanged(job.ctx, job.optionModel, job.tag, job.userChanged);
        } finally {
            job.ctx.config?.Publish(configEvents.controlLostFocus, configEventControlType.picklist);
        }
    };
    return (ctx, optionModel, tag, userChanged) => {
        const wasPending = pending !== null;
        pending = { ctx, optionModel, tag, userChanged };
        if (wasPending) return;
        ctx.config?.Publish(configEvents.controlGotFocus, configEventControlType.picklist);
        const schedule = typeof window.requestAnimationFrame === 'function'
            ? callback => window.requestAnimationFrame(() => window.setTimeout(callback, 0))
            : callback => window.setTimeout(callback, 0);
        schedule(run);
    };
}

function setSizeChangedForConfig(config, tag) {
    config.DoProductSizeChangedWidthValidation(tag);
}

export function setSizeChanged(ctx, tag) {    
    console.log(`Fire size change : ${ctx.productID}`);

    setSizeChangedForConfig(ctx.config, tag);
}



export function onSizeChanged() {

    const fireEvent = debounce(setSizeChanged, 800, { trailing: false });

    return (ctx, sourceKey) => {
        return fireEvent(ctx, sourceKey);
    };

}



export function onSimpleOptionValueChanged() {

    const fireEvent = debounce(setOptionValueChanged, 800, { trailing: false });

    return (ctx, optionModel, newValue, tag) => {

        if (!optionModel.Locked & !optionModel.IsReadOnly) {
            const changed = optionModel.SetValue(window.InsyteConfig.OptionModelSetValueArgs.From(null, accentUtils.isNull(newValue) ? newValue : `${newValue}`));
            return fireEvent(ctx, optionModel, tag, changed);
        }else{
            return Promise.resolve();
        }

    };

}




export function saveOrderLineOptions(line, e) {

    e.OrderLine.SaveValues(e);

    var processedOLOs = [];

    e.OptionsToSave.forEach(function (s) {

        var newOLO = {};

        s.SaveValues(e, newOLO);
        processedOLOs.push(newOLO);

    });

    e.OptionsToDelete.forEach(function (d) {
        //nothing to do
    });


    line.OrderLineOptionsData = JSON.stringify(processedOLOs);

    if (accentUtils.isEmpty(line.MultiProductID)) {

        line.InstallSequence = 0;

        if ((e.ValuesProvider?.IsProductSplitForInstallation()) ?? false) {
            line.InstallSequence = -1; //must be split
        }
    }
}

export function getSizeRequirements(productSize) {

    const requireWidth = productSize?.WidthMeasurementType === window.InsyteConfig.ProductSize_WidthMeasurementTypeValues.Single
        || productSize?.WidthMeasurementType === window.InsyteConfig.ProductSize_WidthMeasurementTypeValues.TopBottom
        || productSize?.WidthMeasurementType === window.InsyteConfig.ProductSize_WidthMeasurementTypeValues.TopMiddleBottom;

    const requireHeight = productSize?.HeightMeasurementType === window.InsyteConfig.ProductSize_HeightMeasurementTypeValues.Single
        || productSize?.HeightMeasurementType === window.InsyteConfig.ProductSize_HeightMeasurementTypeValues.LeftRight
        || productSize?.HeightMeasurementType === window.InsyteConfig.ProductSize_HeightMeasurementTypeValues.LeftMiddleRight;

    const requireMultiWidth = productSize?.WidthMeasurementType === window.InsyteConfig.ProductSize_WidthMeasurementTypeValues.TopBottom
        || productSize?.WidthMeasurementType === window.InsyteConfig.ProductSize_WidthMeasurementTypeValues.TopMiddleBottom;

    const requireMultiHeight = productSize?.HeightMeasurementType === window.InsyteConfig.ProductSize_HeightMeasurementTypeValues.LeftRight
        || productSize?.HeightMeasurementType === window.InsyteConfig.ProductSize_HeightMeasurementTypeValues.LeftMiddleRight;

    return {
        requireWidth,
        requireHeight,
        requireMultiWidth,
        requireMultiHeight
    }

}


export async function applyMultiProductChanges(config, changes, multiPrductInfo, tag) {


    if (accentUtils.isNull(config)) return;

    let lastChange = null;

    try {

        while ((changes?.length ?? 0) > 0) {

            const changeItem = changes.shift();

            lastChange = changeItem;

            if (changeItem.isSize) {

                let sizeUpdated = false;

                if (config.OrderLine.WidthLinkedToSet && !accentUtils.isNull(changeItem.change.width)) {
                    config.OrderLine.Width = changeItem.change.width.newWidth;
                    config.OrderLine.WidthMeasurements = changeItem.change.width.newWidthMeasurements;
                    sizeUpdated = true;
                }

                if (config.OrderLine.HeightLinkedToSet && !accentUtils.isNull(changeItem.change.height)) {
                    config.OrderLine.Height = changeItem.change.height.newHeight;
                    config.OrderLine.HeightMeasurements = changeItem.change.height.newHeightMeasurements;
                    sizeUpdated = true;
                }

                if (sizeUpdated) {
                    setSizeChangedForConfig(config, tag);
                }

            } else {



                const memberProduct = from(multiPrductInfo).where(p => compareValues(p.ID, changeItem.change.sourceConfigID)).firstOrDefault();

                if (accentUtils.isNull(memberProduct)) return;

                const optionModels = config.GetMultiConfigLinkedOptionModelsForChange(
                    memberProduct,
                    changeItem.change.id,
                    changeItem.change.code,
                    changeItem.change.customValue,
                    changeItem.change.additionalValue,
                    changeItem.change.userSet,
                    changeItem.change.supplierID
                );


                for (var i = 0; i < optionModels.length; i++) {

                    const optionModel = optionModels[i];

                    if (!optionModel.Linked) continue;

                    setOptionValueChangedForConfig(config, optionModel, tag);
                }

            }



            //window.InsyteConfig.OptionModelSetValueArgs.From(changeItem.ID, )

        }
    } catch (e) {


        

        const ff = e;
        const gg = ff;

        await showDialog(< ApplyChangesErrorDlg config={config} expection={e} change={lastChange} />);
    }

}

export function fromDecimal(obj) {
    if (!accentUtils.isEmpty(obj)) {

        const v = obj.toString();

        return Number(v);

    }

    return obj;
}




export function fromInt(obj) {
    if (!accentUtils.isEmpty(obj)) {

        const v = obj.toString();

        return Number(v);

    }

    return obj;
}

export function toLength(amount, unit) {
    return window.InsyteConfig.MeasuredValue.FromJson(`{"Value":${accentUtils.isEmpty(amount) ? 0.0 : amount}, "Unit":${unit}}`);
}

export function toArea(amount, unit) {
    return window.InsyteConfig.MeasuredArea.FromJson(`{"Value":${accentUtils.isEmpty(amount) ? 0.0 : amount}, "Unit":${unit}}`);
}

export function toVolume(amount, unit) {
    return window.InsyteConfig.MeasuredVolume.FromJson(`{"Value":${accentUtils.isEmpty(amount) ? 0.0 : amount}, "Unit":${unit}}`);
}



export function isMeasuredValue(value) {
    return window.Bridge.is(value, window.InsyteConfig.MeasuredValue);
}

export function isMeasuredArea(value) {
    return window.Bridge.is(value, window.InsyteConfig.MeasuredArea);
}

export function isMeasuredVolume(value) {
    return window.Bridge.is(value, window.InsyteConfig.MeasuredVolume);
}


export function fromLength(lendth, defaultUnit) {


    let result = {
        Unit: lendth?.Unit ?? defaultUnit,
        Value: typeof lendth === "string" && !accentUtils.isEmpty(lendth) ? Number(lendth) : fromDecimal(lendth?.Value ?? 0),
        Fraction: 0
    };


    const value = result.Value;
    const wholePart = Math.trunc(value);
    const fractionPart = value - wholePart;

    result.Value = wholePart;
    result.Fraction = fractionPart;

    return result;
}

export function toInt(e) {
    return accentUtils.isEmpty(e) ? 0 : window.System.Int32.parse(e.toString());
}

export function toLong(e) {
    return accentUtils.isEmpty(e) ? 0 : window.System.Int64.parse(e.toString());
}

export function toDecimal(e) {
    return accentUtils.isEmpty(e) ? new window.System.Decimal(0) : new window.System.Decimal(e);
}

export function compareValues(v1, v2) {
    return window.Bridge.staticEquals(v1, v2);
}


export function toGuid(e) {
    return accentUtils.isEmpty(e) ? window.System.Guid.empty : window.System.Guid.parse(e);
}

export function isReadOnly(optionModel) {
    return optionModel.IsReadOnly || optionModel.Locked;
}

export function toLengthAsText(length, units, format) {
    return window.InsyteConfig.MeasureFormatting.FormatMeasuredValue(toDecimal(length), toLong(units), toInt(format));    
}

export function getDisplayTypes(){

    return window.InsyteConfig.ProductOptionDisplayTypeValues;
}

export function getMeasureValueUnitTypes() {
    return window.InsyteConfig.MeasureUnitEnumValues.GetMeasuredValueUnits().map(x => {
        const parts = x.split("_");
        return {
            ID: Number(parts[1]),
            Description: parts[0],
            IsMetric: parts[2].toUpperCase() === "TRUE"
        };
    });
}

export function getAreaUnitTypes() {
    return window.InsyteConfig.MeasureUnitEnumValues.GetAreaUnits().map(x => {
        const parts = x.split("_");
        return {
            ID: Number(parts[1]),
            Description: parts[0],
            IsMetric: parts[2].toUpperCase() === "TRUE"
        };
    });
}
export function getVolumeUnitTypes() {
    return window.InsyteConfig.MeasureUnitEnumValues.GetVolumeUnits().map(x => {
        const parts = x.split("_");
        return {
            ID: Number(parts[1]),
            Description: parts[0],
            IsMetric: parts[2].toUpperCase() === "TRUE"
        };
    });
}


export function fabric_GetAllowRailroading(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetAllowRailroading(optionModel);
}

export function fabric_GetAllowContinuous(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetAllowContinuous(optionModel);
}

export function fabric_GetIgnoreFabricWidth(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetIgnoreFabricWidth(optionModel);
}

export function fabric_GetIgnoreRailroadedFabricWidth(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetIgnoreRailroadedFabricWidth(optionModel);
}

export function fabric_GetWidthAdjustment(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetWidthAdjustment(optionModel);
}

export function fabric_GetDropAdjustment(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetDropAdjustment(optionModel);
}

export function fabric_GetRailroadingOption(optionModel) {
    return window.SoftFurnishings.FabricHelper.GetRailroadingOption(optionModel);
}

export function fabric_GetCustomFabric(optionModel, productOptionValue) {
    return window.SoftFurnishings.FabricHelper.GetCustomFabric(optionModel, productOptionValue);
}

export function fabric_OrderLineOptionClear(optionModel) {
    optionModel.OrderLineOption.ProductOptionValueID = toGuid(accentUtils.getEmptyGuid());
    optionModel.OrderLineOption.SupplierID = toGuid(accentUtils.getEmptyGuid());
}
export function fabric_OrderLineOptionSet(optionModel, productOptionValueID, supplierID) {
    optionModel.OrderLineOption.ProductOptionValueID = productOptionValueID;
    optionModel.OrderLineOption.SupplierID = supplierID;
}

export function getOptionModel(ctx, optionID) {
    return ctx.config.GetOptionModel(toGuid(optionID));
}

export function getProductOptionValue(optionModel) {
    return optionModel.OrderLineOption?.ProductOptionValueID ? optionModel.GetProductOptionValue(optionModel.OrderLineOption?.ProductOptionValueID) : null
}

// The accessible name for an option's value control: the option's description
// (what the visible ConfigLabel shows). The label is a div, not a <label>, and the
// same option can render on several lines in the table configurator, so controls
// carry the name themselves (aria-label) rather than pointing at a label id.
export function optionAccessibleName(optionModel, suffix) {
    const description = optionModel?.GetProductOption?.()?.Description ?? "";
    const base = `${description}`.trim();
    if (accentUtils.isEmpty(suffix)) return base || undefined;
    return base ? `${base}: ${suffix}` : suffix;
}


export const useActionType = (optionID) => {


    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, optionID);

    const option = optionModel?.GetProductOption();
    
    // Inline fabric options ("Fabric Inline" / "Fabric 2 Line Inline") keep their own
    // DisplayType in the runtime config but are fabric picker options all the same.
    const isCustomPicker = optionModel.DisplayType === getDisplayTypes().Custom
        || optionModel.DisplayType === "Fabric Inline"
        || optionModel.DisplayType === "Fabric 2 Line Inline";

    return {
        isPicklist: optionModel.DisplayType === getDisplayTypes().Picklist,
        isFabric: isCustomPicker && (option?.CustomDisplayType === fabricSingleLineDisplayType || option?.CustomDisplayType === fabricTwoLineDisplayType),
        isPanelPattern: optionModel.DisplayType === getDisplayTypes().Custom && (option?.CustomDisplayType === panelPatternDisplayType),
    };

};


// Keeps a ref pointing at the latest version of a handler so PubSub subscriptions can be
// registered once (per config) while always invoking the current render's closure. This
// avoids tearing down and re-creating subscriptions on every render, which is O(n) per
// call in the runtime's PubSub and caused multi-hundred-ms hitches on focus/blur.
function useLatestRef(value) {
    const ref = React.useRef(value);
    ref.current = value;
    return ref;
}

// Saving must never continue with unfinished option/size events. The general
// waitForCondition helper stops silently after two seconds, so it is unsuitable
// for this boundary. A stuck config fails the save instead of saving stale data.
export async function waitForConfigIdle(isInProgress) {
    const deadline = Date.now() + 30000;
    do {
        // Also yield when already idle so blur handlers and their React updates
        // are applied before callers validate the rendered configuration.
        await new Promise(resolve => window.setTimeout(resolve, isInProgress() ? 100 : 0));
        if (isInProgress() && Date.now() >= deadline) {
            throw new Error('Configuration changes are still being applied. Please wait and try again.');
        }
    } while (isInProgress());
}

export const useConfigInProgress = (key, config, onChange) => {


    const inProgressCount = React.useRef(0);
    const inProgress = React.useRef(false);
    const [forceRefreshKey, setForceRefreshKey] = React.useState(0);

    const onChangeRef = useLatestRef(onChange);

    const onStart = React.useCallback(() => {

        inProgressCount.current++;
        inProgress.current = inProgressCount.current !== 0;


        if (onChangeRef.current) {
            onChangeRef.current(inProgress.current);
        }

        setForceRefreshKey(x => x + 1);

    }, [onChangeRef]);

    const onFinish = React.useCallback(() => {

        inProgressCount.current--;
        if (inProgressCount.current < 0)
            inProgressCount.current = 0;

        inProgress.current = inProgressCount.current !== 0;


        if (onChangeRef.current) {
            onChangeRef.current(inProgress.current);
        }

        setForceRefreshKey(x => x + 1);

    }, [onChangeRef]);

    React.useEffect(() => {

        if (accentUtils.isNull(config)) return;

        console.log("useConfigInProgress: (Mounted): ", key);


        config.Subscribe(configEvents.controlGotFocus, onStart, `useConfigInProgress_${key}`);
        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ChangeStart, onStart, `useConfigInProgress_${key}`);
        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChanging, onStart, `useConfigInProgress_${key}`);


        config.Subscribe(configEvents.controlLostFocus, onFinish, `useConfigInProgress_${key}`);
        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, onFinish, `useConfigInProgress_${key}`);
        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, onFinish, `useConfigInProgress_${key}`);


        return () => {

            if (accentUtils.isNull(config)) return;

            config.UnSubscribe(configEvents.controlGotFocus, `useConfigInProgress_${key}`);
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChanging, `useConfigInProgress_${key}`);
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ChangeStart, `useConfigInProgress_${key}`);

            config.UnSubscribe(configEvents.controlLostFocus, `useConfigInProgress_${key}`);
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, `useConfigInProgress_${key}`);
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, `useConfigInProgress_${key}`);
        };
    }, [config, key, onStart, onFinish]);


    return inProgress;
}


function getNewValidation() {
    return {
        error: null,
        warning: null,
        stockWarning: null,
        stockError: null,
        required: false
    };
}

function getValidation(optionModel, validationResult) {

    const newValidation = getNewValidation();

    newValidation.required = optionModel.CurrentValueIsNullOrEmpty && optionModel.IsMandatory;

    if (validationResult) {

        if (validationResult.OptionID === optionModel.ID && validationResult.Type !== window.InsyteConfig.ResultType.OK) {

            if (validationResult.Type === window.InsyteConfig.ResultType.Error) {
                newValidation.error = validationResult.Message;
                newValidation.required = true;
            } else if (validationResult.Type === window.InsyteConfig.ResultType.Warning) {
                newValidation.warning = validationResult.Message;
            }
        }
    }
   

    if (!accentUtils.isNull(optionModel.OutOfStockDiscontinuedResult)) {

        if (optionModel.OutOfStockDiscontinuedResult.IsDiscontinued) {
            newValidation.stockError = optionModel.OutOfStockDiscontinuedResult.GetMessage();
        } else {
            newValidation.stockWarning = optionModel.OutOfStockDiscontinuedResult.GetMessage();
        }

    }

    return newValidation;
}



class ValidationManager {

    constructor(optionID, validation, setValidation) {
        this.queue = [];

        this.optionID = optionID;
        this.currentValidation = validation;
        this.notifyValidation = setValidation;

        this.enqueue = this.enqueue.bind(this);
        this.last = this.last.bind(this);
        this.process = this.process.bind(this);
        this.processDebounce = debounce(this.process, 300, { trailing: false });
        

    }

    process() {

        

        const msg = this.last();
        if (compareObjects(msg, this.currentValidation)) return;

        if (this.optionID === "ABCF2FD5-8223-4F09-8E47-0DF2188DA63E") {
            const aa = 1;
            const bb = aa;

            console.log(`ValidationManager: ${msg.Error}`);

        }


        this.notifyValidation(msg);
        this.currentValidation = msg;
    }

    enqueue(validation) {

        if (this.optionID === "ABCF2FD5-8223-4F09-8E47-0DF2188DA63E") {
            const aa = 1;
            const bb = aa;
        }


        this.queue.push(validation);
        this.processDebounce();
    }

    last() {
        const result = from(this.queue).last();

        this.queue = [];

        return result;
    }
}


export const useValidation = (optionID, key) => {


    
    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, optionID);

    const changed = useChanged(optionID, `OptionValidationHook_${key}_${optionID}`, true);
    const sizeChanged = useSizeChanged(optionID, `OptionValidationHook_${key}_${optionID}`);


    const [validation, setValidation] = React.useState(getValidation(optionModel));

    const validationManager = React.useRef(new ValidationManager(optionID, validation, setValidation));


    const onStartValidate = useLatestRef(e => {
        validationManager.current.enqueue(getNewValidation());
    });

    const onValidate = useLatestRef(e => {

        if (optionID?.toUpperCase() === e.OptionID?.toString()?.toUpperCase()) {

            const newValidation = getValidation(optionModel, e);
            validationManager.current.enqueue(newValidation);

        }

    });


    React.useEffect(() => {

        const config = ctx.config;
        const registerKey = `OptionValidationHook_${key}_${optionID}`;

        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Validating_Options, e => onStartValidate.current(e), registerKey);
        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Validate_Option_Control, e => onValidate.current(e), registerKey);

        return () => {
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Validating_Options, registerKey);
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Validate_Option_Control, registerKey)
        };

    }, [optionID, ctx.config, key, onStartValidate, onValidate]);



    return validation;

}


export function toProductValidationItem(result) {

    if (accentUtils.isNull(result)) {
        return null;
    }

    if (typeof result === "string") {
        if (accentUtils.isEmpty(result)) {
            return null;
        }
        return { level: "error", message: result };
    }

    const levelValue = result.Result ?? result.Type;
    if (levelValue === window.InsyteConfig.ResultType.OK) {
        return null;
    }

    if (accentUtils.isEmpty(result.Message)) {
        return null;
    }

    return {
        level: levelValue === window.InsyteConfig.ResultType.Warning ? "warning" : "error",
        message: result.Message
    };
}


class ProductValidationManager {

    constructor(setMessages) {
        this.pending = [];
        this.current = [];
        this.setMessages = setMessages;
        this.process = this.process.bind(this);
        this.processDebounce = debounce(this.process, 300, { trailing: true });
    }

    process() {
        if (compareObjects(this.pending, this.current)) {
            return;
        }
        this.current = this.pending.slice();
        this.setMessages(this.current);
    }

    clear() {
        this.pending = [];
        this.processDebounce();
    }

    add(item) {
        if (this.pending.some(m => m.level === item.level && m.message === item.message)) {
            return;
        }
        this.pending = this.pending.concat(item);
        this.processDebounce();
    }
}


export const useProductValidation = (key) => {

    const ctx = React.useContext(ConfiguratorContext);
    const [messages, setMessages] = React.useState([]);
    const manager = React.useRef(new ProductValidationManager(setMessages));

    const onStartValidate = React.useCallback(() => {
        manager.current.clear();
    }, []);

    const onValidateProduct = React.useCallback(e => {
        const item = toProductValidationItem(e);
        if (item) {
            manager.current.add(item);
        }
    }, []);

    const onProductMessage = React.useCallback(e => {
        const item = toProductValidationItem(e);
        if (item) {
            manager.current.add(item);
        }
    }, []);

    React.useEffect(() => {

        const registerKey = `ProductValidationHook_${key}_${ctx.productID}_${ctx.orderLine.ID}`;

        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Validating_Options, onStartValidate, registerKey);
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Validate_Product, onValidateProduct, registerKey);
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ProductMessages_Message, onProductMessage, registerKey);

        return () => {
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Validating_Options, registerKey);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Validate_Product, registerKey);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ProductMessages_Message, registerKey);
        };

    }, [ctx.config, ctx.productID, ctx.orderLine.ID, key, onStartValidate, onValidateProduct, onProductMessage]);

    return messages;
};


// The vertical view and its optional job sidebar share one collection, fed by
// the engine's existing validation pass. This never starts an extra validation.
export const useConfiguratorIssues = () => {
    const ctx = React.useContext(ConfiguratorContext);
    const [issues, setIssues] = React.useState([]);
    const key = React.useId();
    React.useEffect(() => {
        const config = ctx.config;
        const events = window.InsyteConfig.InsyteConfigEvents;
        const pending = new Map();
        let timer;
        const publish = () => {
            clearTimeout(timer);
            timer = setTimeout(() => setIssues(Array.from(pending.values())), 0);
        };
        const add = (result, field, group, label) => {
            const item = toProductValidationItem(result);
            if (!item) return;
            const id = `${field || "product"}|${item.level}|${item.message}`;
            pending.set(id, { ...item, id, field, group, label });
            publish();
        };
        const subscribe = (event, handler) => {
            if (event) config.Subscribe(event, handler, key);
        };
        const onOption = result => {
            // Validation can refer to repeated extras, whose model cannot be
            // resolved uniquely by option ID. Only the definition is needed here.
            const option = config.Data?.GetProductOption(result.OptionID);
            const field = result.OptionID?.toString().toUpperCase();
            add(result, field, (option?.ProductOptionGroupID ?? option?.GroupID)?.toString(), option?.Description);
        };
        subscribe(events.Validating_Options, () => {
            // ValidateOptions validates the product, size and options in that order.
            pending.clear();
            publish();
        });
        subscribe(events.SizeChanging, () => {
            for (const [id, item] of pending) if (item.field?.startsWith("size:")) pending.delete(id);
            publish();
        });
        subscribe(events.Validate_Option_Control, onOption);
        subscribe(events.Validate_Product, result => add(result));
        subscribe(events.Supplier_Error, result => add(result));
        subscribe(events.SingleLineComposite_Error, result => add(result));
        subscribe(events.ProductMessages_Message, result => add(result));
        subscribe(events.Size_Width_Error, result => add(result, "size:width", "size", "Width"));
        subscribe(events.Size_Height_Error, result => add(result, "size:height", "size", "Height"));
        subscribe(events.Size_Area_Error, result => add(result, "size:width", "size", "Size"));
        subscribe(events.Size_General_Error, result => add(result, "size:width", "size", "Size"));
        // Refresh header selection summaries even when the issue set is unchanged.
        subscribe(events.ChangeOccurred, publish);
        subscribe(events.SizeChangeOccurred, publish);
        return () => {
            clearTimeout(timer);
            [events.Validating_Options, events.SizeChanging, events.Validate_Option_Control,
                events.Validate_Product, events.Supplier_Error, events.SingleLineComposite_Error,
                events.ProductMessages_Message, events.Size_Width_Error,
                events.Size_Height_Error, events.Size_Area_Error, events.Size_General_Error,
                events.ChangeOccurred, events.SizeChangeOccurred].filter(Boolean)
                .forEach(event => config.UnSubscribe(event, key));
        };
    }, [ctx.config, key]);
    return issues;
};

export function focusConfiguratorIssue(container, issue) {
    const requestedField = issue?.field ?? issue?.optionID;
    if (!container || !requestedField) return false;
    const key = requestedField.toString().toUpperCase();
    const fieldKey = key === "WIDTH" ? "SIZE:WIDTH" : key === "DROP" || key === "HEIGHT" ? "SIZE:HEIGHT" : key;
    const findField = () => Array.from(container.querySelectorAll("[data-config-field]"))
        .find(element => element.dataset.configField.toUpperCase() === fieldKey);
    const field = findField();
    if (!field) return false;
    field.dispatchEvent(new CustomEvent("config-focus-field", { bubbles: true }));
    // A closing transition can be reversed by Go to field. Its completion can
    // then exceed the normal 350ms, so do not focus an input in a hidden Collapse.
    // Resolve again because filtering can replace the field during the transition.
    const focusWhenOpen = attemptsLeft => {
        const target = findField();
        if (!target?.isConnected) return;
        if (target.closest(".collapsing, .collapse:not(.show)")) {
            if (attemptsLeft > 0) window.setTimeout(() => focusWhenOpen(attemptsLeft - 1), 50);
            return;
        }
        target.scrollIntoView?.({ behavior: "smooth", block: "center" });
        const value = target.querySelector(".config-option-value-container, .config-size-value-container") || target;
        const control = value.querySelector('input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled)')
            || value.querySelector('[role="radio"][aria-checked="true"]:not(:disabled), button:not(:disabled)');
        (control || target).focus({ preventScroll: true });
    };
    window.setTimeout(() => focusWhenOpen(20), 400);
    return true;
}

// Slide open / closed. Height animates to its content so a panel with many issues and
// one with a single issue both settle at their natural size.
const issueSummaryTransition = { duration: 0.22, ease: [0.4, 0, 0.2, 1] };
const issueSummaryCollapsed = { height: 0, opacity: 0, marginBottom: 0 };
const issueSummaryExpanded = { height: "auto", opacity: 1, marginBottom: 12 };
const issueItemCollapsed = { height: 0, opacity: 0, marginTop: 0 };
const issueItemExpanded = { height: "auto", opacity: 1, marginTop: 10 };

export const ConfiguratorIssueSummary = ({ issues = [], onGoToField }) => {
    const reduceMotion = useReducedMotion();
    const transition = reduceMotion ? { duration: 0 } : issueSummaryTransition;
    const errors = issues.filter(item => item.level === "error").length;
    const warnings = issues.length - errors;
    const keyOf = (issue, index) => issue.id || `${issue.field || issue.optionID || ""}:${issue.message}:${index}`;
    // The panel and each item are animated containers; the visual box is the inner element
    // so the border and padding are not squashed while the height collapses.
    return <AnimatePresence initial={false}>
        {issues.length > 0 && <motion.div key="config-issue-summary" className="config-issue-summary-slot"
            initial={issueSummaryCollapsed} animate={issueSummaryExpanded} exit={issueSummaryCollapsed} transition={transition}>
            <aside className={classNames("config-issue-summary", { "has-warning-only": !errors })} aria-label="Configuration issues" aria-live="polite">
                <div className="config-issue-summary-title">{errors ? `${errors} ${errors === 1 ? "issue" : "issues"} to fix` : `${warnings} ${warnings === 1 ? "warning" : "warnings"}`}
                    {errors > 0 && warnings > 0 && ` · ${warnings} ${warnings === 1 ? "warning" : "warnings"}`}</div>
                <AnimatePresence initial={false}>
                    {issues.map((issue, index) => <motion.div key={keyOf(issue, index)} className="config-issue-summary-item-slot"
                        initial={issueItemCollapsed} animate={issueItemExpanded} exit={issueItemCollapsed} transition={transition}>
                        <div className={`config-issue-summary-item ${issue.level}`}>
                            {issue.label && <strong>{issue.label}</strong>}
                            <div>{issue.message}</div>
                            {(issue.field || issue.optionID) && onGoToField && <button type="button" className="config-text-button"
                                onClick={() => onGoToField(issue)} aria-label={`Go to ${issue.label || "field"}`}>Go to field</button>}
                        </div>
                    </motion.div>)}
                </AnimatePresence>
            </aside>
        </motion.div>}
    </AnimatePresence>;
};

export const ProductValidationBanner = React.memo(props => {

    const messages = useProductValidation(props.asTableRow ? "table" : "vertical");

    if (messages.length === 0) {
        return null;
    }

    const hasError = messages.some(m => m.level === "error");
    const body = (
        <div className={classNames("config-product-validation", {
            "has-error": hasError,
            "has-warning-only": !hasError,
            "compact": props.asTableRow
        })}>
            {messages.map((m, i) => (
                <div key={`${m.level}_${i}`} className={classNames("config-product-validation-item", m.level)}>
                    {m.level === "warning"
                        ? <AccentIcons.Warning outlined />
                        : <AccentIcons.Error outlined />}
                    <span>{m.message}</span>
                </div>
            ))}
        </div>
    );

    if (props.asTableRow) {
        return (
            <tr className="config-product-validation-row">
                <td colSpan={99}>{body}</td>
            </tr>
        );
    }

    return body;
});



export const useSizeChanged = (optionID, key, tag = null) => {

    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, optionID);
    const [changeID, setChangeID] = React.useState(0);
    const [hasChanged, setHasChanged] = React.useState(false);

    const onChanged = useLatestRef(e => {

        if ((!accentUtils.isNull(tag) && tag !== e.Tag) || (optionModel?.RefreshRequired ?? false)) {
            setChangeID(x => x + 1);
            setHasChanged(true);
        } else {
            setHasChanged(false);
        }

    });

    React.useEffect(() => {

        const config = ctx.config;
        const registerKey = `OptionSizeChangedHook_${key}_${optionID}`;

        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, e => onChanged.current(e), registerKey);

        return () => {
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, registerKey);
        };

    }, [optionID, ctx.config, key, onChanged]);


    return {
        changeID: `changeID_${changeID}`,
        hasChanged: hasChanged,
        prevValue: optionModel?.PrevValue,
        newValue: optionModel?.CurrentValueAsString
    };

}


export const useRequireRefresh = (optionID, key) => {

    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, optionID);

    const [refreshKey, setRefreshKey] = React.useState(0);

    React.useEffect(() => {

        if (optionModel.RefreshRequired) {
            setRefreshKey(x => x + 1);
        }

    }, [setRefreshKey, optionModel.RefreshRequired, ctx.isInProgress])

    

    return {
        refreshID: refreshKey
    };

}


export const useRefreshKeyExpression = (optionID, expression, valueKnownByCaller) => {

    const ctx = React.useContext(ConfiguratorContext);
    const optionModel = getOptionModel(ctx, optionID);

    const modelRefreshKey = optionModel.RefreshKey;

    const [refreshKey, setRefreshKey] = React.useState(0);

    // This is the most up-to-date value from the model, based on the expression.
    const currentValueFromModel = expression(optionModel);

    // This ref will store the last value we've seen and acknowledged, either from the caller or from a model change.
    const lastKnownValueRef = React.useRef(valueKnownByCaller);

    // This effect synchronizes our internal "known value" with the value provided by the caller.
    // A change in the caller's known value should not by itself trigger a refresh.
    React.useEffect(() => {
        lastKnownValueRef.current = valueKnownByCaller;
    }, [valueKnownByCaller]);

    // This effect checks for a change in the model's value against our last known value.
    // This is what triggers the refresh.
    React.useEffect(() => {
        // Explicitly convert both values to strings for a consistent comparison.
        const modelValueAsString = currentValueFromModel?.toString() ?? "";
        const knownValueAsString = lastKnownValueRef.current?.toString() ?? "";

        if (modelValueAsString !== knownValueAsString) {
            setRefreshKey(x => x + 1);
            // Once we've triggered a refresh, the new model value becomes our last known value.
            lastKnownValueRef.current = currentValueFromModel;
        }
    }, [currentValueFromModel]);

    return {
        refreshID: `${refreshKey}_${modelRefreshKey}`
    };
};

export const useRefreshKey = (optionID, valueKnownByCaller) => {
    // Call the generalized hook, passing the specific expression and the externally managed known value.
    return useRefreshKeyExpression(optionID, model => model.CurrentValueAsString, valueKnownByCaller);
};

// Tracks a visibility value derived from the configurator runtime (e.g. IsGroupVisibleForOrder or
// a Locked && HideIfDisabled check). Recomputes on ChangeOccurred/SizeChangeOccurred, but only
// stores (and therefore re-renders) when the computed visibility actually flips.
export const useVisibleForOrder = (computeVisible, key) => {

    const ctx = React.useContext(ConfiguratorContext);

    const computeRef = React.useRef(computeVisible);
    computeRef.current = computeVisible;

    const [visible, setVisible] = React.useState(() => computeVisible());

    React.useEffect(() => {

        const registerKey = `useVisibleForOrder_${key}`;

        const onRecompute = () => {
            const v = computeRef.current();
            setVisible(prev => (prev === v ? prev : v));
        };

        // Runs after the runtime has re-evaluated (Before/AfterValueChanged and size equivalents
        // complete before these events publish), so Locked state and group visibility are fresh.
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, onRecompute, registerKey);
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, onRecompute, registerKey);

        onRecompute();

        return () => {
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, registerKey);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, registerKey);
        };

    }, [ctx.config, key]);

    return visible;
};

// True when the runtime has disabled (locked) the option and the option is flagged to hide
// rather than render read-only.
export const isHiddenWhenDisabled = (option, optionModel) => {
    return (option?.HideIfDisabled ?? false) && (optionModel?.Locked ?? false);
};


const debugValueGroups = [
    { prefix: "runtime-var:", title: "Show/Hide Vars" },
    { prefix: "runtime-segment:", title: "Segments" },
    { prefix: "runtime-keyword:", title: "Keywords" },
    { prefix: "config-option:", title: "Config Options" },
    { prefix: "runtime-option:", title: "Runtime Options" },
];

function readDebugValues(config) {

    const result = [];

    try {

        const pairs = configFrom(config.GetDebugValuesByName()).toArray();

        pairs.forEach(kv => {
            const key = kv.key ?? kv.Key;
            const value = kv.value ?? kv.Value;

            if (!accentUtils.isNull(key)) {
                result.push({ key: `${key}`, value: accentUtils.isNull(value) ? "" : `${value}` });
            }
        });

    } catch (e) {
        console.log("readDebugValues failed", e);
    }

    return result.sort((a, b) => a.key.localeCompare(b.key));
}

// Debug side panel showing the configurator runtime state (segments, show/hide vars, option
// values). Refreshes after each change/size event. Intended for the product test screen only.
export const ConfigDebugPanel = () => {

    const ctx = React.useContext(ConfiguratorContext);

    const [values, setValues] = React.useState(() => readDebugValues(ctx.config));
    const [filter, setFilter] = React.useState("");

    React.useEffect(() => {

        const registerKey = `ConfigDebugPanel_${ctx.productID}_${ctx.orderLine.ID}`;

        const onRecompute = () => {
            setValues(readDebugValues(ctx.config));
        };

        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, onRecompute, registerKey);
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, onRecompute, registerKey);

        return () => {
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, registerKey);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, registerKey);
        };

    }, [ctx.config, ctx.productID, ctx.orderLine.ID]);

    const filterUpper = filter.trim().toUpperCase();
    const filtered = filterUpper === ""
        ? values
        : values.filter(v => v.key.toUpperCase().includes(filterUpper) || v.value.toUpperCase().includes(filterUpper));

    const groups = debugValueGroups
        .map(g => ({ ...g, items: filtered.filter(v => v.key.startsWith(g.prefix)) }))
        .filter(g => g.items.length > 0);

    const grouped = groups.flatMap(g => g.items);
    const other = filtered.filter(v => !grouped.includes(v));

    if (other.length > 0) {
        groups.push({ prefix: "", title: "Other", items: other });
    }

    const rowStyle = { display: "flex", gap: "0.5rem", borderBottom: "1px solid #eee", padding: "1px 0" };
    const keyStyle = { flex: "0 0 55%", overflowWrap: "anywhere" };
    const valueStyle = { flex: "1 1 auto", overflowWrap: "anywhere", color: "#666" };

    return (
        <div className="config-debug-panel" style={{ fontFamily: "monospace", fontSize: "12px" }}>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
                <strong>Runtime Debug</strong>
                <input
                    type="text"
                    placeholder="filter..."
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    style={{ flex: "1 1 auto", padding: "2px 6px" }}
                />
                <span style={{ color: "#666" }}>{filtered.length}</span>
            </div>
            {groups.map(g => (
                <div key={g.title} style={{ marginBottom: "0.75rem" }}>
                    <div style={{ fontWeight: "bold", background: "#f2f2f2", padding: "2px 4px" }}>{g.title} ({g.items.length})</div>
                    {g.items.map(v => (
                        <div key={v.key} style={rowStyle}>
                            <div style={keyStyle}>{g.prefix === "" ? v.key : v.key.substring(g.prefix.length)}</div>
                            <div style={valueStyle}>{v.value}</div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};


export const useChanged = (optionID, key, notifyIfMe, ignoreTag) => {

    const tag = React.useRef(newGuid());
    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, optionID);


    const [changeID, setChangeID] = React.useState(0);
    const [hasChanged, setHasChanged] = React.useState(false);

    const requireRefresh = useRequireRefresh(optionID, key);


    const onChanged = useLatestRef(e => {

        const wasMe = e.ID.toString().toUpperCase() === optionID.toUpperCase() && (ignoreTag || tag.current === e.Tag);

        if (optionModel.RefreshRequired && (notifyIfMe || !wasMe)) {
            if (!wasMe) {
                setChangeID(x => x + 1);
            }
            setHasChanged(true);
        } else {
            setHasChanged(false);
        }

    });

    React.useEffect(() => {

        const config = ctx.config;
        const registerKey = `OptionChangedHook_${key}_${optionID}`;

        config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, e => onChanged.current(e), registerKey);

        return () => {
            config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, registerKey);
        };

    }, [optionID, ctx.config, key, onChanged]);


    return {
        changeID: `changeID_${changeID}_${requireRefresh.refreshID}`,
        prevValue: optionModel.PrevValue,
        newValue: optionModel.CurrentValueAsString,
        hasChanged: hasChanged,
        tag: tag.current
    };

}



export const ConfigLabel = props => {

    const option = props.option;    
    const label = option.Description;


    return <div className="config-label" data-optionid={option?.ID?.toString()}>
        <IndicatorBar optionID={ option?.ID?.toString() } />
        <div className="config-label-text">{label}</div>        
        <ConfigInfo option={option} expanded={props.helpExpanded} onToggle={props.onHelpToggle} helpID={props.helpID} />
    </div>

};

export const IndicatorBar = React.memo(props => {

    return<div className="config-indicator-bar">
            <ChangeWarningIndicator optionID={ props.optionID} />
            <ErrorWarningIndicator optionID={props.optionID} />
            <RequiredIndicator optionID={props.optionID} />
            <OptionLinkIndicator optionID={props.optionID} />
        </div>;
});

export const RequiredIndicator = props => {


    const ctx = React.useContext(ConfiguratorContext)
    const optionModel = getOptionModel(ctx, props.optionID);

    let indicator = "";

    if (optionModel.MandatoryForQuoting) {
        indicator = "*";
    } else if (optionModel.MandatoryForOrdering) {
        indicator = "**"
    }

    const className = classNames("config-required-indicator",
        {
            "quoting": indicator.length === 1,
            "ordering": indicator.length === 2,
        }
    );

    return <div className={className} >
        { indicator}
    </div>

};

export const ChangeWarningIndicator = props => {

    const ctx = React.useContext(ConfiguratorContext);
    const optionModel = getOptionModel(ctx, props.optionID);
    const changed = useChanged(props.optionID, "ChangeWarningIndicator", false, true);
    const sizeChanged = useSizeChanged(props.optionID, "ChangeWarningIndicator");

    const onDelete = React.useCallback(() => {

        const deleted = ctx.config.DeleteObsoleteOption(optionModel.ID);

        if (deleted && ctx.notifyDirty) {
            ctx.notifyDirty();
        }

        if (deleted && ctx.refresh) {
            ctx.refresh();
        }

    }, [ctx, optionModel]);

    const beforeAndAfterIsEmpty = (accentUtils.isEmpty(changed.prevValue) && accentUtils.isEmpty(changed.prevValue) === accentUtils.isEmpty(changed.newValue)) || (accentUtils.isEmpty(sizeChanged.prevValue)  && accentUtils.isEmpty(sizeChanged.prevValue) === accentUtils.isEmpty(sizeChanged.newValue));

    let prev = null;

    if (changed.hasChanged) {
        prev = accentUtils.isEmpty(changed.prevValue) ? "<EMPTY>" : changed.prevValue;
    }

    if (sizeChanged.hasChanged) {
        prev = accentUtils.isEmpty(sizeChanged.prevValue) ? "<EMPTY>" : sizeChanged.prevValue;
    }

    const hasValue = (changed.hasChanged || sizeChanged.hasChanged) && !beforeAndAfterIsEmpty;

    const msg = !ctx.isReadOnly && optionModel.IsObsoleteExistingOption
        ? <AccentIcons.Delete
            top="0"
            style={{ color: "Red" }}
            toolTipText="Remove discontinued option from line"
            onClick={onDelete}
            noPadding
        />
        : <ConfigMessageIndicator message={`Changed from ${prev}`} changed />;

    const animation = (!hasValue && !optionModel.IsObsoleteExistingOption) ? "none" : "displayed";

    if (!hasValue && !optionModel.IsObsoleteExistingOption) return null;

    if (props.optionID === "7A5BA7B3-14A7-4902-97A7-0C46008077F8") {
        const aa = 1;
        const bb = aa;
    }


    return <motion.div className="config-change-indicator"
        key={props.optionID}
        initial={{ opacity: 0, scale: 0 }}
        animate={animation}
        variants={{
            "none": {
                opacity: 0,
                scale: [1, 0],
                transition: { duration: 1}
            },
            "displayed": {
                opacity: 1,
                scale: [0, 1]
            }
        }}
        transition={{
            duration: 1
        }}
    >
        {msg}
    </motion.div>    

};





export const SizeIndicator = React.memo(props => {

    const ctx = React.useContext(ConfiguratorContext)

    const [error, setError] = React.useState(null);


    const clearError = React.useCallback(e => {
        setError(null);

    }, [setError]);


    const onError = React.useCallback(e => {
        setError(e);

    }, [setError]);


    React.useEffect(() => {

        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChanging, clearError, `SizeIndicator_${props.width}_${ctx.productID}`);



        if (props.width)
            ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Size_Width_Error, onError, `SizeIndicator_${props.width}_${ctx.productID}`);

        if (!props.width)
            ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Size_Height_Error, onError, `SizeIndicator_${props.width}_${ctx.productID}`);

        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Size_Area_Error, onError, `SizeIndicator_${props.width}_${ctx.productID}`);
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.Size_General_Error, onError, `SizeIndicator_${props.width}_${ctx.productID}`);

        return () => {

            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChanging, `SizeIndicator_${props.width}_${ctx.productID}`);

            if (props.width)
                ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Size_Width_Error, `SizeIndicator_${props.width}_${ctx.productID}`);
            if (!props.width)
                ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Size_Height_Error, `SizeIndicator_${props.width}_${ctx.productID}`);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Size_Area_Error, `SizeIndicator_${props.width}_${ctx.productID}`);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.Size_General_Error, `SizeIndicator_${props.width}_${ctx.productID}`);

        };

    }, [ctx.productID]);


    let ctrl = null;

    
    if (!accentUtils.isEmpty(error)) {
        ctrl = <SizeErrorIndicator id={'${props.width}_${ctx.productID}'} error={error} />;
    } else {
        ctrl = <div></div>;
    }


    let linkedSizeCtrl = null;

    if (props.width) {
        linkedSizeCtrl = <SizeLinkBtn noPadding getLinked={ol => ol.WidthLinkedToSet} toggleLinked={ol => {
            ol.WidthLinkedToSet = !ol.WidthLinkedToSet;
        }} />;
    } else {
        linkedSizeCtrl = <SizeLinkBtn noPadding getLinked={ol => ol.HeightLinkedToSet} toggleLinked={ol => {
            ol.HeightLinkedToSet = !ol.HeightLinkedToSet;
        }} />;
    }
    

    return <div className={`config-size-indicator`} >
        <div></div>        
        {ctrl}
        <div>{linkedSizeCtrl}</div>
    </div>;

});



export const ErrorWarningIndicator = React.memo(props => {

    const validation = useValidation(props.optionID, "ErrorWarningIndicator");

    //const type = !accentUtils.isEmpty(validation.error) ? "error" : !accentUtils.isEmpty(validation.warning) ? "warning" : "empty";

    let ctrl = null;

    if (!accentUtils.isEmpty(validation.error)) {
        ctrl = <ErrorIndicator id={props.optionID} error={ validation.error} />;
    } else if (!accentUtils.isEmpty(validation.warning)) {
        ctrl = <WarningIndicator id={props.optionID} warning={ validation.warning} />;
    } else {
        ctrl = null;
    }





    return <div className={`config-error-warning-indicator`} >
        <AnimatePresence>
            {ctrl}
        </AnimatePresence>
    </div>;

});

export const WarningIndicator = props => {

    return <motion.div
        className="config-warning-indicator"
        key={`warning_${props.id}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: [0, 1.5, 1] }}
        exit={{ opacity: 0, scale: [1, 0], rotate: [360, 720, 1080, 2160, 2520, 0] }}
        transition={{ duration: 1 }}
    >
        <ConfigMessageIndicator message={props.warning} warning />
    </motion.div>   
};

export const ErrorIndicator = React.memo(props => {


    return <motion.div
        className="config-error-indicator"
        key={`error_${props.id}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: [0, 1.5, 1] }}
        exit={{ opacity: 0, scale: [1, 0], rotate: [360, 720, 1080, 2160, 2520, 0] }}
        transition={{ duration: 1 }}
    >
        <ConfigMessageIndicator message={props.error} />
    </motion.div>
});

export const SizeErrorIndicator = React.memo(props => {


    return <motion.div
        className="config-error-indicator"
        key={`error_${props.id}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: [0, 1.5, 1] }}        
        transition={{ duration: 1 }}
    >
        <ConfigMessageIndicator message={props.error} />
    </motion.div>
});

export const ConfigMessageIndicator = ({ message, warning, changed }) => {
    const display = useAccentDisplay();
    const focusDetails = React.useCallback(element => element?.focus({ preventScroll: true }), []);
    const anchor = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const title = changed ? "Value changed" : warning ? "Warning" : "Issue";
    const close = () => { setOpen(false); anchor.current?.focus(); };
    return <>
        <button type="button" ref={anchor} className="config-icon-button"
            aria-label={changed ? "Show previous value" : `Show ${title.toLowerCase()}`} aria-expanded={open}
            onClick={() => setOpen(value => !value)} onKeyDown={e => { if (e.key === "Escape") close(); }}>
            {changed ? <AccentIcons.WarningChanged noPadding /> : warning ? <AccentIcons.Warning outlined /> : <AccentIcons.Error outlined />}
        </button>
        {open && <AccentPopup anchor={anchor} show={open} onAutoClose={() => setOpen(false)}
            headerContent={title} maxWidth="min(360px, 90vw)"
            anchorAlign={{ horizontal: "left", vertical: "bottom" }} popupAlign={{ horizontal: "left", vertical: "top" }}>
            <div className="config-message-details" ref={focusDetails} tabIndex={-1} onKeyDown={e => { if (e.key === "Escape") close(); }}>
                <div>{typeof message === "string" ? message : message?.Message}</div>
                {display.mobile && <button type="button" className="config-text-button" onClick={close}>Close</button>}
            </div>
        </AccentPopup>}
    </>;
};

export const ConfigHelpNote = ({ notes, id, onHide }) => <div className="config-help-note" id={id}>
    <span>{notes}</span><button type="button" className="config-text-button" onClick={onHide}>Hide</button>
</div>;

export const ConfigInfo = props => {

    const notes = props.option?.Notes;
    const [localExpanded, setExpanded] = React.useState(false);
    const anchor = React.useRef(null);
    const localID = React.useId();
    const expanded = props.onToggle ? props.expanded : localExpanded;
    const toggle = props.onToggle || (() => setExpanded(value => !value));
    const id = props.helpID || localID;
    const previousExpanded = React.useRef(expanded);
    React.useEffect(() => {
        if (previousExpanded.current && !expanded) anchor.current?.focus();
        previousExpanded.current = expanded;
    }, [expanded]);

    if (accentUtils.isEmpty(notes)) return null;

    return <div className="config-info">
        <button type="button" ref={anchor} className="config-icon-button config-help-button" aria-label={`Help for ${props.option?.Description || "option"}`}
            aria-expanded={!!expanded} aria-controls={id} onClick={toggle}><AccentIcons.Info /></button>
        {expanded && !props.onToggle && <ConfigHelpNote notes={notes} id={id} onHide={toggle} />}
    </div>;

};

export const SizeLinkBtn = props => {

    
    const ctx = React.useContext(ConfiguratorContext);
    const multiCtx = ctx.getMultiConfigManager();

    const orderLine = ctx.config.OrderLine;

    const [linked, setLinked] = React.useState(props.getLinked(orderLine));
    



    const onToggle = e => {

        props.toggleLinked(orderLine);
        setLinked(l=> !l);
        ctx.notifyDirty();
    };



    if (accentUtils.isNull(multiCtx)) return null;

    if (!multiCtx.allowSizeLinking(orderLine)) return null;


    if (linked) {
        return <AccentIcons.Link top={ props.top} style={{ color: "Blue", ...props.style }} onClick={onToggle} noPadding={props.noPadding} />;
    }

    return <AccentIcons.LinkBroken top={props.top} style={{ color: "Red", ...props.style }} onClick={onToggle} noPadding={props.noPadding} />;
};

export const OptionLinkIndicator = props => {

    

    const ctx = React.useContext(ConfiguratorContext);
    const multiCtx = ctx.getMultiConfigManager();
    const isMultiProduct = !accentUtils.isNull(multiCtx);

    const optionModel = getOptionModel(ctx, props.optionID);

    const [linked, setLinked] = React.useState(optionModel.Linked);


    

    const onToggle = e => {
        optionModel.Linked = !optionModel.Linked;

        setLinked(optionModel.Linked);

        ctx.notifyDirty();
    };



    let content = null;


    if (isMultiProduct) {

        const showLink = multiCtx.optionHasLinks(props.optionID, ctx.config.Data.ID);

        if (showLink) {
            if (linked) {
                content = <AccentIcons.Link top="0" style={{ color: "Blue" }} onClick={onToggle} noPadding />;
            } else {
                content = <AccentIcons.LinkBroken top="0" style={{ color: "Red" }} onClick={onToggle} noPadding />;
            }
        }

        return <div className="config-linked-indicator">
            {content}
        </div>;

    }

    return null;

};


export const ActionBar = props => {

    return <div className="config-action-bar">
        <ValueActionBtn optionID={props.optionID} />
        <FabricDetailsActionBtn optionID={props.optionID} />
        <PanelPatternActionBtn optionID={props.optionID} />
    </div>
};


export const configFilterTypes = {
    all: "all",
    quote: "quote",
    order: "order",
    invalid: "invalid",
    userSet: "userSet",
    changed: "changed",
    calculations: "calculations",
};

export function isOptionVisible(optionModel, filter) {

    // IsVisible === false is the definition's IsVisibleExpression evaluating false
    // (null expression = visible); that alone hides the option here. Locked (a
    // failed enable/dependency rule) does NOT hide by itself — a locked option
    // renders read-only unless the option is flagged HideIfDisabled, which the
    // wrappers apply via isHiddenWhenDisabled. The strict === false guard keeps
    // engines that predate IsVisible rendering everything.
    if (optionModel.IsVisible === false) return false;

    if ((filter?.length??0) === 0) return true;


    let result = false;

    const showCalculations = from(filter).any(f => f === configFilterTypes.calculations);

    for (var i = 0; i < filter.length; i++) {

        if (!showCalculations && (optionModel.Calculated || optionModel.HasCalculatedScript)) {
            return false;
        }


        if (filter[i] === configFilterTypes.all)
            return true;

        if (filter[i] === configFilterTypes.order && (optionModel.MandatoryForOrdering || optionModel.MandatoryForQuoting))
            return true;

        if (filter[i] === configFilterTypes.quote && optionModel.MandatoryForQuoting)
            return true;

        if (filter[i] === configFilterTypes.userSet && optionModel.UserChanged)
            return true;

        if (filter[i] === configFilterTypes.invalid && optionModel.IsInValid)
            return true;

        if (filter[i] === configFilterTypes.changed && optionModel.IsDirty)
            return true;

        
    }


    return result;

}


export const ConfigFilterBar = props => {

    const [option, setOption] = React.useState(accentUtils.isNull(props.defaultValue) ? configFilterTypes.all : from(props.defaultValue).where(x => x !== configFilterTypes.calculations).firstOrDefault() ?? configFilterTypes.all);
    const [edit, setEdit] = React.useState(accentUtils.isNull(props.defaultValue) ? configFilterTypes.calculations : from(props.defaultValue).where(x => x === configFilterTypes.calculations).firstOrDefault());


    const onEditChange = React.useCallback(e => {
        const newValue = edit === configFilterTypes.calculations ? null : configFilterTypes.calculations;
        setEdit(newValue);
        props.onChange([newValue, option].filter(x => !accentUtils.isNull(x)));
    }, [setEdit, option, edit])

    const onOptionChange = React.useCallback(e => {
        setOption(e);
        props.onChange([edit, e].filter(x => !accentUtils.isNull(x)));
    }, [setOption, option, edit])

    const iconStyle = { padding: "3px" };

    return <ButtonToolbar className="config-filter">
        <ButtonGroup className="me-2">
            <AccentButton color="primary" tagName="configFilterAll" outline active={option === configFilterTypes.all} onClick={() => onOptionChange(configFilterTypes.all)} tooltip={t(`application_strings.application.controls.configFilter.${configFilterTypes.all}_tooltip`)}>
                {t(`application_strings.application.controls.configFilter.${configFilterTypes.all}`)}
            </AccentButton>
            <AccentButton color="primary" tagName="configFilterQuote" outline active={option === configFilterTypes.quote} onClick={() => onOptionChange(configFilterTypes.quote)} tooltip={t(`application_strings.application.controls.configFilter.${configFilterTypes.quote}_tooltip`)}>
                {t(`application_strings.application.controls.configFilter.${configFilterTypes.quote}`)}
            </AccentButton>
            <AccentButton color="primary" tagName="configFilterOrder" outline active={option === configFilterTypes.order} onClick={() => onOptionChange(configFilterTypes.order)} tooltip={t(`application_strings.application.controls.configFilter.${configFilterTypes.order}_tooltip`)}>
                {t(`application_strings.application.controls.configFilter.${configFilterTypes.order}`)}
            </AccentButton>
            <AccentButton color="primary" tagName="configFilterInvalid" outline active={option === configFilterTypes.invalid} onClick={() => onOptionChange(configFilterTypes.invalid)} tooltip={t(`application_strings.application.controls.configFilter.${configFilterTypes.invalid}_tooltip`)}>
                <AccentIcons.Warning style={iconStyle} />                
            </AccentButton>
            <AccentButton color="primary" tagName="configFilterChanged" outline active={option === configFilterTypes.changed} onClick={() => onOptionChange(configFilterTypes.changed)} tooltip={t(`application_strings.application.controls.configFilter.${configFilterTypes.changed}_tooltip`)}>
                <AccentIcons.WarningChanged style={iconStyle} />                
            </AccentButton>
        </ButtonGroup>
        <ButtonGroup className="me-2">
            <AccentButton color="primary" tagName="configFilterCalc" outline active={edit === configFilterTypes.calculations} onClick={() => onEditChange()} tooltip={t(`application_strings.application.controls.configFilter.${configFilterTypes.calculations}_tooltip`)}>
                <AccentIcons.Calculate style={iconStyle} />
            </AccentButton>
        </ButtonGroup>
        {props.showDebugToggle &&
            <ButtonGroup className="me-2">
                <AccentButton color="primary" tagName="configFilterDebug" outline active={props.debugActive} onClick={() => props.onDebugChange()} tooltip="Show/hide runtime debug values">
                    <AccentIcons.Info style={iconStyle} />
                </AccentButton>
            </ButtonGroup>}
    </ButtonToolbar>;
};

export const denominators = [
    { ID: 1, Value: 1 },
    { ID: 0.5, Value: 2 },
    { ID: 0.25, Value: 4 },
    { ID: 0.125, Value: 8 },
    { ID: 0.0625, Value: 16 },
    { ID: 0.03125, Value: 32 },
    { ID: 0.015625, Value: 64 }
];


function simplifyFraction(numerator, denominator) {
    if (numerator === 0) return "0";
    function gcd(a, b) {
        return b ? gcd(b, a % b) : a;
    }
    const divisor = gcd(numerator, denominator);
    return `${numerator / divisor}/${denominator / divisor}`;
}

export  function generateFractions(denominator) {
    return Array.from({ length: denominator }, (_, i) => {
        const id = (i / denominator);

        // Special case for 0
        if (i === 0) return { ID: id, Description: "0" };

        let description = `${i}/${denominator}`;
        const simplified = simplifyFraction(i, denominator);

        if (simplified !== `${i}/${denominator}`) {
            description = `${i}/${denominator} (${simplified})`;
        }

        return { ID: id, Description: description };
    });
}


export function getFractions(precision) {

    const denominator = denominators.find(x => x.ID === precision);

    return generateFractions(denominator?.Value ?? 16);

}

